/**
 * Forge dual-read of CMMD #5456 runtime Sprite identity.
 *
 * CMMD's forge-context runtime DTO projects `spriteName` / `spriteId` beside the
 * temporary `flyMachineId` bridge. After that projection:
 *
 * - Sprite rows carry a non-empty `spriteName` (filled from `sprite_name` or,
 *   during transition, from the bridge column).
 * - Historical `substrate='fly'` rows get `spriteName: null` and must stay
 *   fail-closed. Do not reclassify them by reading `flyMachineId`.
 *
 * When `spriteName` is present on the payload (including explicit null), that
 * value wins. Fall back to `flyMachineId` only when the new field is absent so
 * a pre-#5456 CMMD still works during the consumer migration (forge#3501).
 *
 * Adapters and fly_* columns stay until CMMD #5458. Do not remove them here.
 */

export type CmmdRuntimeSpriteIdentitySource = {
  readonly spriteName?: string | null;
  readonly spriteId?: string | null;
  readonly flyMachineId?: string | null;
  readonly metadata?: Readonly<Record<string, unknown>> | null;
};

export type CmmdRuntimeSpriteOperationalIdentity = {
  readonly spriteName: string | null;
  readonly spriteId: string | null;
};

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function spriteIdFromMetadata(
  metadata: Readonly<Record<string, unknown>> | null | undefined,
): string | null {
  if (!metadata || typeof metadata !== "object") return null;
  return nonEmptyString(metadata.spriteId);
}

/**
 * Resolve the operational Sprite name for provision / connect / readiness gates.
 *
 * @see CmmdRuntimeSpriteIdentitySource for the dual-contract rules.
 */
export function resolveCmmdRuntimeSpriteName(
  runtime: Pick<CmmdRuntimeSpriteIdentitySource, "spriteName" | "flyMachineId">,
): string | null {
  if (Object.hasOwn(runtime, "spriteName")) {
    return nonEmptyString(runtime.spriteName);
  }
  return nonEmptyString(runtime.flyMachineId);
}

/**
 * Resolve Sprite name and id the same way CMMD dual-read helpers do for Sprites
 * rows. forge-context public DTO omits substrate and already nulls sprite_* for
 * historical Fly, so an explicit null `spriteName` stays fail-closed here.
 */
export function resolveCmmdRuntimeSpriteIdentity(
  runtime: CmmdRuntimeSpriteIdentitySource,
): CmmdRuntimeSpriteOperationalIdentity {
  const spriteName = resolveCmmdRuntimeSpriteName(runtime);
  if (spriteName === null && Object.hasOwn(runtime, "spriteName")) {
    return { spriteName: null, spriteId: null };
  }
  return {
    spriteName,
    spriteId: nonEmptyString(runtime.spriteId) ?? spriteIdFromMetadata(runtime.metadata),
  };
}

/** True when the payload carries an operational Sprite name after dual-read. */
export function hasCmmdRuntimeSpriteIdentity(
  runtime: Pick<CmmdRuntimeSpriteIdentitySource, "spriteName" | "flyMachineId">,
): boolean {
  return resolveCmmdRuntimeSpriteName(runtime) !== null;
}
